import {
  Divider,
  IconButton,
  List,
  ListItemIcon,
  ListItemText,
  MenuItem,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC, SyntheticEvent, useCallback } from 'react';
import {
  MdAccessTimeFilled,
  MdMoreVert as MoreVert,
  MdVisibilityOff,
} from 'react-icons/md';
import { ReactComponent as ExclusionsIcon } from '../../../../assets/icons/exclusions.svg';
import { ReactComponent as PolicyXIcon } from '../../../../assets/icons/policyX.svg';
import ExtendedMenuItem from '../../../../elements/OxMenu/ExtendedMenuItem';
import OxMenuControlled from '../../../../elements/OxMenu/OxMenuControlled';
import SubMenuItem from '../../../../elements/OxMenu/SubMenuItem';
import { openCreateJiraTicketDialog } from '../../actions/create-jira-ticket-modal-actions';
import { openSendSlackMessageDialog } from '../../actions/slack-actions';
import {
  openQuickFixModal,
  setApplicationIrrelevantModal,
  setCommentModal,
  setFilesDiffModalVisibility,
  setPolicyDisableModal,
  setPolicyFixModal,
} from '../../../active-issues/store-actions/issue-store-actions';
import {
  setExcludeCategoryID,
  setExcludeCategoryName,
} from '../../store-actions/issues-exclusions-store-actions';
import { Exclusions, Issue } from '../../types/issues-types';
import { SiJira as JiraIcon } from 'react-icons/si';
import { SiSlack as SlackIcon } from 'react-icons/si';
import {
  setIssueDrawer,
  setMenuIssueId,
} from '../../../active-issues/actions/active-issues-actions';
import { BiCodeAlt as BiCodeAltIcon } from 'react-icons/bi';
import { FaFlag, FaRegCommentDots } from 'react-icons/fa';
import { DiGit } from 'react-icons/di';
import { isDevelopment } from 'env-utils';
import { SlMagicWand } from 'react-icons/sl';
import { ReactComponent as PolicyEdit } from '../../../../assets/icons/policy-edit.svg';
import { AppPages } from '../../../../common/routing/app-navigator';

const IssueOptions: FC<React.PropsWithChildren<IssueOptionsProps>> = ({
  withOptions,
  exclusionsCategories,
  handleExcludeAlert,
  isActiveJiraConnection,
  isActiveSlackConnection,
  isPRAvailable,
  fixIssue,
  issueId,
  handleOpenMenu,
  handleCloseMenu,
  open,
  isFixAvailable,
  categoryId,
  policyId,
}) => {
  const handleDisablePolicy = useCallback(async () => {
    setMenuIssueId(null);
    setPolicyDisableModal(true);
    setMenuIssueId(null);
  }, []);

  const handleViewEditPolicy = useCallback(async () => {
    setMenuIssueId(null);
    const win = window.open(
      `${window.location.origin}${AppPages.Policies}?categoryId=${categoryId}&policyId=${policyId}`,
      '_blank',
    );
    if (win != null) {
      win.focus();
    }
  }, [categoryId, policyId]);

  const handleApplicationIrrelevant = useCallback(async () => {
    await setIssueDrawer(issueId);
    setApplicationIrrelevantModal(true);
    setMenuIssueId(null);
  }, [issueId]);

  const chooseExcludeAlert = useCallback(
    async (category: Exclusions) => {
      await setIssueDrawer(issueId);
      setExcludeCategoryID(category.id);
      setExcludeCategoryName(category.label);
      if (handleExcludeAlert) {
        handleExcludeAlert();
      }
      setMenuIssueId(null);
    },
    [handleExcludeAlert, issueId],
  );

  const handleSendSlackMessage = useCallback(async () => {
    await setIssueDrawer(issueId);
    openSendSlackMessageDialog();
    setMenuIssueId(null);
  }, [issueId]);

  const handleCreateJiraTicket = useCallback(async () => {
    await setIssueDrawer(issueId);
    openCreateJiraTicketDialog();
    setMenuIssueId(null);
  }, [issueId]);

  const openFilesDiffModal = useCallback(async () => {
    await setIssueDrawer(issueId);
    setFilesDiffModalVisibility(true);
    setMenuIssueId(null);
  }, [issueId]);

  const handleAddComment = useCallback(async () => {
    await setIssueDrawer(issueId);
    setCommentModal(true);
  }, [issueId]);

  const openPrModal = useCallback(async () => {
    await setIssueDrawer(issueId);

    openQuickFixModal();
    setMenuIssueId(null);
  }, [issueId]);

  const openPolicyFix = useCallback(async () => {
    await setIssueDrawer(issueId);
    setPolicyFixModal(true);
    setMenuIssueId(null);
  }, [issueId]);

  const reportAsFalsePositive = useCallback(() => {}, []);

  if (withOptions) {
    return null;
  }
  return (
    <>
      <OxMenuControlled
        onClose={handleCloseMenu}
        open={open}
        trigger={
          <IconButton onClick={handleOpenMenu} size='small'>
            <MoreVert />
          </IconButton>
        }>
        <div>
          <MenuItem onClick={handleAddComment}>
            <ListItemIcon>{<FaRegCommentDots />}</ListItemIcon>
            <ListItemText>Add Comment</ListItemText>
          </MenuItem>
          {isDevelopment() && (
            <MenuItem onClick={openPolicyFix} disabled={!isFixAvailable}>
              <ListItemIcon>
                <SlMagicWand />
              </ListItemIcon>
              <ListItemText>Fix Issue</ListItemText>
            </MenuItem>
          )}
          {isDevelopment() && (
            <Tooltip
              arrow
              placement='bottom'
              title={
                isPRAvailable
                  ? ''
                  : 'Automatic code fix recommendation unavailable'
              }>
              <span>
                <MenuItem
                  onClick={openFilesDiffModal}
                  disabled={!isPRAvailable}>
                  <ListItemIcon>
                    <BiCodeAltIcon size='1.2rem' />
                  </ListItemIcon>
                  <ListItemText>View Code Fix</ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          )}
          <Divider />
          {isDevelopment() && (
            <Tooltip
              arrow
              placement='bottom'
              title={
                isPRAvailable
                  ? ''
                  : 'Automatic code fix recommendation unavailable'
              }>
              <span>
                <MenuItem onClick={openPrModal} disabled={!isPRAvailable}>
                  <ListItemIcon>
                    <DiGit size='1.2rem' />
                  </ListItemIcon>
                  <ListItemText>Open PR</ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          )}

          <Tooltip
            arrow
            placement='bottom'
            title={isActiveJiraConnection ? '' : 'Jira is not connected'}>
            <span>
              <MenuItem
                disabled={!isActiveJiraConnection}
                onClick={handleCreateJiraTicket}>
                <ListItemIcon>
                  <SvgIcon component={JiraIcon} fontSize='inherit' />
                </ListItemIcon>
                <ListItemText>Create Jira Ticket</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          {isDevelopment() && (
            <Tooltip
              arrow
              placement='bottom'
              title={isActiveSlackConnection ? '' : 'Slack is not connected'}>
              <span>
                <MenuItem
                  disabled={!isActiveSlackConnection}
                  onClick={handleSendSlackMessage}>
                  <ListItemIcon>
                    <SvgIcon component={SlackIcon} fontSize='inherit' />
                  </ListItemIcon>
                  <ListItemText>Send to Slack</ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          )}
          <Divider />

          <ExtendedMenuItem
            id='exclusion-menu'
            withIcon
            subMenu={
              <List>
                {exclusionsCategories?.map((category, index) => {
                  return (
                    <SubMenuItem
                      onClick={() => chooseExcludeAlert(category)}
                      key={index}
                      data-type={category.type}>
                      <Typography variant='body1' color='text.primary'>
                        {category.label}
                      </Typography>
                    </SubMenuItem>
                  );
                })}
              </List>
            }>
            <ListItemIcon>
              <SvgIcon fontSize='inherit'>
                <ExclusionsIcon />
              </SvgIcon>
            </ListItemIcon>
            <ListItemText>Exclude Issue</ListItemText>
          </ExtendedMenuItem>
          <ExtendedMenuItem
            id='exclusion-menu'
            withIcon
            subMenu={
              <List>
                {exclusionsCategories?.map((category, index) => {
                  return (
                    <SubMenuItem
                      onClick={() => chooseExcludeAlert(category)}
                      key={index}
                      data-type={category.type}>
                      <Typography variant='body1' color='text.primary'>
                        {category.label}
                      </Typography>
                    </SubMenuItem>
                  );
                })}
              </List>
            }>
            <ListItemIcon>
              <MdAccessTimeFilled />
            </ListItemIcon>
            <ListItemText>Snooze Issue</ListItemText>
          </ExtendedMenuItem>
          <MenuItem onClick={handleApplicationIrrelevant}>
            <ListItemIcon>
              <MdVisibilityOff />
            </ListItemIcon>
            <ListItemText>Make App Irrelevant</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDisablePolicy}>
            <ListItemIcon>
              <SvgIcon component={PolicyXIcon} fontSize='small' />
            </ListItemIcon>
            <ListItemText>Disable Policy</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleViewEditPolicy}>
            <ListItemIcon>
              <SvgIcon component={PolicyEdit} fontSize='small' />
            </ListItemIcon>
            <ListItemText>View/Edit Policy</ListItemText>
          </MenuItem>
          {isDevelopment() && (
            <MenuItem onClick={reportAsFalsePositive} disabled>
              <ListItemIcon>
                <FaFlag />
              </ListItemIcon>
              <ListItemText>Report As False Positive</ListItemText>
            </MenuItem>
          )}
        </div>
      </OxMenuControlled>
    </>
  );
};

export interface IssueOptionsProps {
  policyDisableModal?: boolean;
  withOptions?: boolean;
  exclusionsCategories?: Exclusions[];
  handleExcludeAlert?: () => void;
  isActiveJiraConnection?: boolean;
  isActiveSlackConnection?: boolean;
  isPRAvailable: boolean;
  issueId: Issue['id'];
  fixIssue: Issue['fixIssue'];
  handleOpenMenu: (e: SyntheticEvent) => void;
  handleCloseMenu: () => void;
  open: boolean;
  isFixAvailable: boolean;
  categoryId: string;
  policyId?: string;
}

export default IssueOptions;
